import { ReactElement, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import SectionHeader from 'components/SectionHeader';
import SessionNavigation from 'components/SessionNavigation';
import { chipVariants } from 'components/chipVariants';
import RunGroupSection from './RunGroupSection';
import { useInvalidateSession, useSessionSimple, useSessionsWithConfigs } from '../../../hooks/useSessions';
import { useSessionNavigation } from '../../../hooks/useSessionNavigation';
import { useWatchSettlingRuns } from '../../../hooks/useWatchSettlingRuns';
import { getSessionTypeName } from '../../../utils/sessionIconUtils';
import { groupRuns, RunGroupMode } from '../../../utils/runGrouping';

const GROUP_MODE_KEY = 'sessionSimpleGroupMode';
const GROUP_MODES: Array<{ mode: RunGroupMode; label: string }> = [
  { mode: 'date', label: 'Date' },
  { mode: 'week', label: 'Week' },
  { mode: 'month', label: 'Month' },
];

// Simplified session page for the freeride / discarded team singletons: no
// form or summary cards, just the session's runs grouped by date/week/month.
export default function SessionSimple({ sessionId }: { sessionId: string }): ReactElement {
  // Dummy form context so RunEditMenu's ConfigSelector (useFormContext) is safe.
  const formMethods = useForm();

  const { data: session, isPending, isFetching } = useSessionSimple(sessionId);
  const invalidateSession = useInvalidateSession(sessionId);
  const { data: sessionsWithConfigs } = useSessionsWithConfigs();
  const { previousSessionId, nextSessionId } = useSessionNavigation(sessionId);

  const [groupMode, setGroupModeState] = useState<RunGroupMode>(() => {
    const stored = sessionStorage.getItem(GROUP_MODE_KEY);
    return stored === 'week' || stored === 'month' ? stored : 'date';
  });
  const setGroupMode = (mode: RunGroupMode) => {
    sessionStorage.setItem(GROUP_MODE_KEY, mode);
    setGroupModeState(mode);
  };

  // Singletons have exactly one config; all runs live there.
  const config = session?.configs?.[0];
  const groups = useMemo(() => groupRuns(config?.runs ?? [], groupMode), [config?.runs, groupMode]);

  // This page has no poll of its own — the ReprocessProvider watch is what
  // turns a run's "Processing…" row into a real timestamp without a refresh.
  useWatchSettlingRuns(config?.runs);

  const title = session ? getSessionTypeName(session.analysis_type) : 'Session';

  return (
    <FormProvider {...formMethods}>
      <div className="cr-mx-auto cr-max-w-5xl cr-px-4">
        <SectionHeader
          title={title}
          isLoading={isPending || isFetching}
          backLink={{ to: '/sessions', text: 'Back to Sessions' }}
          actions={
            <SessionNavigation
              previousSessionId={previousSessionId}
              nextSessionId={nextSessionId}
            />
          }
        />
        <div className="cr-mb-6 cr-flex cr-items-center cr-gap-2">
          <span className="cr-font-main cr-text-sm cr-text-blue-900">Group by:</span>
          {GROUP_MODES.map(({ mode, label }) => (
            <div
              key={mode}
              className={chipVariants({ active: groupMode === mode })}
              onClick={() => setGroupMode(mode)}
            >
              {label}
            </div>
          ))}
        </div>
        {!isPending && groups.length === 0 && (
          <div className="cr-py-12 cr-text-center cr-font-main cr-text-gray-500">
            No runs in this session yet.
          </div>
        )}
        {groups.map((group, index) => (
          <RunGroupSection
            key={`${groupMode}-${group.key}`}
            group={group}
            config={config}
            session={session}
            sessions={sessionsWithConfigs}
            invalidateRuns={invalidateSession}
            defaultExpanded={index === 0}
          />
        ))}
      </div>
    </FormProvider>
  );
}
